import { useState, useEffect } from 'react'
import OutsideClickHandler from 'react-outside-click-handler/build/OutsideClickHandler'
import { useNavigate } from 'react-router-dom'
import { handleError } from 'src/Common/function'
import FormTemplateService from 'src/Services/API/formtemplate.service'
import FormTypeDropdown from 'src/Pages/forms/templates/components/FormTypeDropdown'
import FormFrequencyDropdown from 'src/Pages/forms/templates/components/FormFrequencyDropdown'
import TemplateDropdown from './components/TemplateDropdown'

const FormCreateModal = ({
  shown,
  woId,
  onClose,
  initialTemplateId = null,
  initialTemplateSchema = null,
  initialFormType = null,
  initialFrequency = '',
}) => {
  const navigate = useNavigate()

  const [templates, setTemplates] = useState([])

  const [selectedTemplateId, setSelectedTemplateId] = useState(null)
  const [selectedTemplateSchema, setSelectedTemplateSchema] = useState(null)
  const [name, setName] = useState('')
  const [formType, setFormType] = useState(null)
  const [frequency, setFrequency] = useState('')

  useEffect(() => {
    if (!shown) return
    setSelectedTemplateId(initialTemplateId)
    setSelectedTemplateSchema(initialTemplateSchema)
    setName('')
    setFormType(initialFormType)
    setFrequency(initialFrequency)

    FormTemplateService.getAll('true', '')
      .then((res) => {
        if (res.status === 200) setTemplates(res.data)
      })
      .catch((err) => handleError(err))
  }, [shown, initialTemplateId, initialTemplateSchema, initialFormType, initialFrequency])

  const handleTemplateSelect = (templateId) => {
    setSelectedTemplateId(templateId)
    const tpl = templates.find((t) => t.id === templateId)
    if (tpl) {
      setSelectedTemplateSchema(tpl.schema ?? null)
      setFormType(tpl.formType ?? null)
      setFrequency(tpl.frequency !== null && tpl.frequency !== undefined ? String(tpl.frequency) : '')
      return
    }
    setSelectedTemplateSchema(null)
  }

  const isDisabled = !name.trim() || formType === null

  const handleCreate = async () => {
    if (isDisabled) return

    let templateSchema = selectedTemplateSchema
    if (selectedTemplateId && (templateSchema === null || templateSchema === undefined)) {
      try {
        const res = await FormTemplateService.getById(selectedTemplateId)
        if (res.status === 200) {
          templateSchema = res.data?.schema ?? null
        }
      } catch (err) {
        handleError(err)
        return
      }
    }

    const path = woId ? `/form-builder/${woId}` : '/form-builder'
    const state = {
      formName: name.trim(),
      formType,
      frequency: frequency !== '' ? Number(frequency) : null,
      templateId: selectedTemplateId,
      from: woId ? `/work/${woId}` : '/all-forms',
    }
    if (selectedTemplateId) state.formSchema = templateSchema
    if (woId) {
      state.woId = woId
      state.status = 1
    }
    navigate(path, { state })
    onClose()
  }

  if (!shown) return null

  const templateOptions = templates.map((t) => ({ value: t.id, label: t.name }))

  return (
    <div className="fixed w-screen h-screen left-0 top-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
      <OutsideClickHandler onOutsideClick={onClose}>
        <div className="w-[436px] bg-white rounded-[6px] p-[18px] flex flex-col">
          <p className="text-[16px] font-OpenSansMedium font-[600] text-titleColor mb-5">
            CREATE FORM
          </p>

          <div className="w-full mb-4">
            <p className="text-[12px] font-OpenSansMedium font-[600] text-titleColor mb-2">
              Start from a template
            </p>
            <TemplateDropdown
              options={templateOptions}
              selectedValue={selectedTemplateId}
              onSelect={handleTemplateSelect}
              placeholder="Choose template"
            />
          </div>

          <div className="w-full mb-4">
            <p className="text-[12px] font-OpenSansMedium font-[600] text-titleColor mb-2">
              Form name <span className="text-red-500">*</span>
            </p>
            <div className="w-full h-[30px] border-solid border-[1px] rounded-[5px] flex items-center justify-center px-[15px]">
              <input
                value={name}
                type="text"
                onChange={(e) => setName(e.target.value)}
                className="w-full text-inputEmailTitle text-[11px] font-[400] font-OpenSansMedium"
                placeholder="Enter form name"
              />
            </div>
          </div>

          <div className="w-full mb-4">
            <p className="text-[12px] font-OpenSansMedium font-[600] text-titleColor mb-2">
              Form Type <span className="text-red-500">*</span>
            </p>
            <FormTypeDropdown
              selectedValue={formType}
              onSelect={(val) => {
                setFormType(val)
                if (val !== 0) setFrequency('')
              }}
            />
          </div>

          {formType === 0 && (
            <div className="w-full mb-2">
              <p className="text-[12px] font-OpenSansMedium font-[600] text-titleColor mb-2">
                Frequency <span className="text-red-500">*</span>
              </p>
              <FormFrequencyDropdown
                selectedValue={frequency}
                onSelect={setFrequency}
              />
            </div>
          )}

          <div className="w-full mt-3">
            <div className="flex items-center justify-between gap-4">
              <div
                onClick={onClose}
                className="w-[50%] h-[30px] text-[10px] rounded-[6px] border-[1px] border-subTitleColor flex items-center justify-center text-subTitleColor cursor-pointer"
              >
                CANCEL
              </div>
              <div
                onClick={() => { if (!isDisabled) handleCreate() }}
                className={`w-[50%] h-[30px] text-[10px] rounded-[6px] flex items-center justify-center ${isDisabled
                  ? 'bg-gray-300 text-white cursor-not-allowed'
                  : 'bg-blueColor text-white cursor-pointer'
                  }`}
              >
                CREATE
              </div>
            </div>
          </div>
        </div>
      </OutsideClickHandler>
    </div>
  )
}

export default FormCreateModal
